import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import InputTextField from "../../../../../components/input/inputTextField";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../../../components/button/ButtonCustoms";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../../../components/alert/swal";
import {
  setLoading,
  unsetLoading,
} from "../../../../../store/loading/loading.slice";
import ModalCustom from "../../../../../components/modal/ModalCustom";
import InputDatePicker from "../../../../../components/input/inputDatePicker";
import moment from "moment";
import { settingLeavePeriodApi } from "../../../../../api/setting-leave-period/setting-leave-period.api";
import CheckBoxCustom from "../../../../../components/checkbox/CheckBoxCustom";

type Props = {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void; // Accepts a boolean argument
  actionType: string;
  data?: {
    id_leave_period: number;
    passing_year: boolean;
    year: string;
    start_date: string;
    end_date: string;
  };
  onFetch: () => void;
};

const ModalSetting: React.FC<Props> = (props: Props) => {
  const { isOpenModal, setIsOpenModal, actionType, data, onFetch } = props;
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [isCanEdit, setIsCanEdit] = useState<boolean>(false);

  const [formSettingLeave, setFormSettingLeave] = useState<{
    passing_year: boolean;
    year: string;
    start_date: string;
    end_date: string;
  }>({
    passing_year: false,
    year: "",
    start_date: "",
    end_date: "",
  });

  const handleChange = (name: string, value: string) => {
    setErrorMessage(false);
    setFormSettingLeave((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (
      formSettingLeave.year &&
      formSettingLeave.start_date &&
      formSettingLeave.end_date
    ) {
      const body: {
        leave_period: string;
        passing_year: boolean;
        leave_period_start: string;
        leave_period_end: string;
        status: string;
      } = {
        leave_period: formSettingLeave.year,
        passing_year: formSettingLeave.passing_year,
        leave_period_start: formSettingLeave.start_date,
        leave_period_end: formSettingLeave.end_date,
        status: "active",
      };

      await settingLeavePeriodApi
        .createLeavePeriod(body)
        .then((res) => {
          if (res.status < 300) {
            dispatch(unsetLoading());
            alertSuccess(
              "ตั้งค่าวันลา",
              "ตั้งค่าวันลา เรียบร้อยแล้ว",
              undefined,
              () => {
                dispatch(unsetLoading());
                setIsOpenModal(false);
                setFormSettingLeave({
                  year: "",
                  passing_year: false,
                  start_date: "",
                  end_date: "",
                });
                onFetch();
              }
            );
          }
        })
        .catch((err) => {
          alertError("ตั้งค่าวันลา ไม่สำเร็จ", err?.response?.data?.message || "");
        })
        .finally(() => {
          dispatch(unsetLoading());
        });
    } else {
      setErrorMessage(true);
      alertError("ตั้งค่าวันลา ไม่สำเร็จ กรุณาใส่ข้อมูลที่จำเป็น");
    }
  };

  const handleUpdate = async (id: number) => {
    if (id) {
      const body: {
        leave_period: string;
        passing_year: boolean;
        leave_period_start: string;
        leave_period_end: string;
        status: string;
      } = {
        leave_period: formSettingLeave.year,
        passing_year: formSettingLeave.passing_year,
        leave_period_start: formSettingLeave.start_date,
        leave_period_end: formSettingLeave.end_date,
        status: "active",
      };

      await settingLeavePeriodApi
        .updateLeavePeriod(id, body)
        .then((res) => {
          if (res.status < 300) {
            dispatch(unsetLoading());
            alertSuccess(
              "แก้ไขการตั้งค่าวันลา",
              "แก้ไขการตั้งค่าวันลา เรียบร้อยแล้ว",
              undefined,
              () => {
                dispatch(unsetLoading());
                setIsOpenModal(false);
                setFormSettingLeave({
                  year: "",
                  passing_year: false,
                  start_date: "",
                  end_date: "",
                });
                setIsCanEdit(false);
                onFetch();
              }
            );
          }
        })
        .catch((err) => {
          alertError("แก้ไขการตั้งค่าวันลา ไม่สำเร็จ", err?.response?.data?.message || "");
        })
        .finally(() => {
          dispatch(unsetLoading());
        });
    } else {
      alertError("แก้ไขการตั้งค่าวันลา ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    if (actionType === "update" && data) {
      setFormSettingLeave({
        year: data.year,
        passing_year: data.passing_year,
        start_date: moment(data.start_date).format("YYYY-MM-DD"),
        end_date: moment(data.end_date).format("YYYY-MM-DD"),
      });
    }
  }, [actionType, data]);

  const handleDeleteLeavePerid = async () => {
    if (data?.id_leave_period) {
      setIsOpenModal(false);

      alertActive(
        "ลบตั้งค่าวันลา",
        "ท่านต้องการลบตั้งค่าวันลา ใช่หรือไม่",
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await settingLeavePeriodApi
            .deleteLeavePeriod(+data?.id_leave_period)
            .then((res) => {
              if (res.status < 300) {
                alertSuccess(
                  "ลบตั้งค่าวันลา เรียบร้อย",
                  undefined,
                  undefined,
                  () => {
                    onFetch();
                    setIsOpenModal(false);
                    setIsCanEdit(false);
                  }
                );
              }
            })
            .catch(() => {
              alertError("ตั้งค่าวันลา ไม่สำเร็จ");
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  return (
    <ModalCustom
      title={
        actionType === "update" ? `รอบปีตั้งค่าวันลา` : `เพิ่มรอบปีตั้งค่าวันลา`
      }
      subTitle="กรุณาระบุข้อมูลให้ครบถ้วนก่อนการยืนยัน"
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
        setIsCanEdit(false);
      }}
      component={
        <section className="grid grid-cols-12 gap-y-6">
          {actionType === "create" && (
            <article className="col-span-12">
              <CheckBoxCustom
                checked={formSettingLeave.passing_year}
                label="ดึงข้อมูลการตั้งค่าจากปีที่แล้ว"
                setChecked={(status: boolean) => {
                  setFormSettingLeave({
                    ...formSettingLeave,
                    passing_year: status,
                  });
                }}
              />
            </article>
          )}

          <article className="col-span-12">
            <InputTextField
              value={formSettingLeave.year}
              helperText={
                errorMessage && !formSettingLeave.year
                  ? "กรุณาใส่ข้อมูลที่จำเป็น"
                  : ""
              }
              type={"number"}
              heading="รอบปี"
              required
              placeholder="ระบุรอบปี"
              onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange("year", e.target.value)
              }
              maxLength={4}
              disabled={!isCanEdit && actionType === "update"}
            />
          </article>
          <article className="col-span-12">
            <InputDatePicker
              heading={"วันที่เริ่มต้น"}
              placeholder="ระบุวันที่เริ่มต้น"
              value={formSettingLeave.start_date}
              helperText={
                errorMessage && !formSettingLeave.start_date
                  ? "กรุณาใส่ข้อมูลที่จำเป็น"
                  : ""
              }
              required
              allowClear
              onChange={(e: any) => {
                handleChange("start_date", moment(e).format("YYYY-MM-DD"));
              }}
              disabled={!isCanEdit && actionType === "update"}
            />
          </article>
          <article className="col-span-12">
            <InputDatePicker
              heading={"วันที่สิ้นสุด"}
              placeholder="ระบุวันที่สิ้นสุด"
              value={formSettingLeave.end_date}
              required
              allowClear
              onChange={(e: any) => {
                handleChange("end_date", moment(e).format("YYYY-MM-DD"));
              }}
              helperText={
                errorMessage && !formSettingLeave.end_date
                  ? "กรุณาใส่ข้อมูลที่จำเป็น"
                  : ""
              }
              disabled={!isCanEdit && actionType === "update"}
            />
          </article>
          {actionType === "update" ? (
            <article className="col-span-12 flex items-center justify-center gap-4 my-4">
              <ButtonOutlined
                onClick={() => {
                  setIsCanEdit(false);
                  setIsOpenModal(false);
                  setErrorMessage(false);
                  setFormSettingLeave({
                    year: "",
                    passing_year: false,
                    start_date: "",
                    end_date: "",
                  });
                }}
                mode="del"
                buttonName="ย้อนกลับ"
                btnProps={{
                  width: "100px",
                }}
              />

              {isCanEdit && (
                <ButtonPrimary
                  onClick={() => {
                    handleUpdate(data ? data.id_leave_period : 0);
                  }}
                  buttonName={"บันทึก"}
                  btnProps={{
                    width: "100px",
                  }}
                  mode="next"
                />
              )}

              {!isCanEdit && (
                <ButtonPrimary
                  onClick={() => {
                    setIsCanEdit(true);
                  }}
                  buttonName={"แก้ไข"}
                  btnProps={{
                    width: "100px",
                  }}
                />
              )}

              {!isCanEdit && (
                <ButtonPrimary
                  onClick={() => {
                    handleDeleteLeavePerid();
                  }}
                  mode="del"
                  buttonName={"ลบ"}
                  btnProps={{
                    width: "100px",
                  }}
                />
              )}
            </article>
          ) : (
            <article className="col-span-12 flex items-center justify-center gap-4 my-4">
              <ButtonOutlined
                onClick={() => {
                  setIsOpenModal(false);
                  setIsCanEdit(false);
                  setErrorMessage(false);
                  setFormSettingLeave({
                    year: "",
                    passing_year: false,
                    start_date: "",
                    end_date: "",
                  });
                }}
                mode="del"
                buttonName="ยกเลิก"
              />
              <ButtonPrimary
                onClick={() => {
                  handleSubmit();
                }}
                mode="success"
                buttonName="ยืนยัน"
              />
            </article>
          )}
        </section>
      }
    />
  );
};

export default ModalSetting;
